// ─────────────────────────────────────────────────────────────────────────────
// ARCHIVO: C:\Users\josemontedeoca\rrhh-system\frontend\src\components\public\VacantesPublicas.jsx
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import postulacionesPublicasService from '../../api/postulacionesPublicasService';
import {
  Briefcase, Search, MapPin, Calendar, ChevronDown, ChevronRight,
  ArrowRight, X, Filter, AlertCircle, Clock, Building2,
  Users, Award, Globe, Loader
} from 'lucide-react';

// ─── Colores institucionales ProDominicana ────────────────────────────────────
const C = {
  azul:       '#1B3A6B',
  azulMed:    '#2251A3',
  azulLight:  '#EEF3FB',
  rojo:       '#DC143C',
  rojoLight:  '#FEF0F3',
  gris:       '#64748B',
  grisClaroS: '#F8FAFC',
  grisClaro:  '#F1F5F9',
  grisBorde:  '#E2E8F0',
  texto:      '#1E293B',
  textoMed:   '#475569',
  blanco:     '#FFFFFF',
};

// ─── Normaliza campos del backend (PascalCase o camelCase) ───────────────────
const normalizar = (v) => ({
  id:          v.VacanteID        ?? v.id,
  titulo:      v.Titulo           ?? v.titulo           ?? v.title        ?? 'Sin título',
  descripcion: v.Descripcion      ?? v.descripcion      ?? v.description  ?? '',
  requisitos:  v.Requisitos       ?? v.requisitos       ?? '',
  modalidad:   v.Modalidad        ?? v.modalidad        ?? 'Presencial',
  estado:      v.Estado           ?? v.estado           ?? 'Activa',
  fecha:       v.FechaPublicacion ?? v.fechaPublicacion ?? v.createdAt    ?? '',
  departamento:v.Departamento     ?? v.departamento     ?? v.category     ?? 'General',
  provincia:   v.Provincia        ?? v.provincia        ?? 'Santo Domingo',
  salarioMin:  v.SalarioMinimo    ?? v.salarioMinimo    ?? null,
  salarioMax:  v.SalarioMaximo    ?? v.salarioMaximo    ?? null,
});

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtFecha = (str) => {
  if (!str) return '';
  const d = new Date(str);
  if (isNaN(d)) return str;
  return d.toLocaleDateString('es-DO', { day: 'numeric', month: 'long', year: 'numeric' });
};
const truncate = (text = '', max = 115) =>
  text.length > max ? text.slice(0, max).trimEnd() + '…' : text;

// ─── Badge de modalidad ───────────────────────────────────────────────────────
const MODAL_STYLE = {
  Presencial: { bg: '#EEF3FB', color: '#1B3A6B', border: '#BFDBFE' },
  Híbrido:    { bg: '#F0FDF4', color: '#15803D', border: '#BBF7D0' },
  Remoto:     { bg: '#FFF7ED', color: '#C2410C', border: '#FED7AA' },
};
const getBadge = (mod) => MODAL_STYLE[mod] || { bg: '#F1F5F9', color: '#475569', border: '#E2E8F0' };

// ─── Skeleton Card ────────────────────────────────────────────────────────────
const SkeletonCard = () => (
  <div style={st.card}>
    <div style={{ height: 4, background: '#E2E8F0', borderRadius: '12px 12px 0 0', margin: '-1px -1px 0', position: 'relative', top: -1 }} />
    <div style={{ marginTop: 14 }}>
      {[['55%', 12], ['82%', 17], ['95%', 11], ['68%', 11]].map(([w, h], i) => (
        <div key={i} style={{ ...st.skeletonLine, width: w, height: h, marginBottom: 10 }} />
      ))}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20, paddingTop: 12, borderTop: '1px solid #E2E8F0' }}>
        <div style={{ ...st.skeletonLine, width: 80, height: 10 }} />
        <div style={{ ...st.skeletonLine, width: 100, height: 34, borderRadius: 8 }} />
      </div>
    </div>
  </div>
);

// ─── Botón Postularme ─────────────────────────────────────────────────────────
const BtnPostular = ({ onClick }) => {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '7px 16px', borderRadius: 8, whiteSpace: 'nowrap',
        border: `1.5px solid ${hov ? C.rojo : C.azul}`,
        background: hov ? C.rojo : C.blanco,
        color: hov ? C.blanco : C.azul,
        fontSize: 13, fontWeight: 700, cursor: 'pointer',
        fontFamily: 'inherit', transition: 'all .18s',
      }}
    >
      Postularme <ArrowRight size={13} />
    </button>
  );
};

// ─── Botones navbar ───────────────────────────────────────────────────────────
const BtnNav = ({ label, onClick, outline }) => {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        padding: '7px 16px', borderRadius: 8, fontFamily: 'inherit',
        fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all .15s',
        border: outline ? `1.5px solid ${hov ? '#CBD5E1' : '#E2E8F0'}` : 'none',
        background: outline ? '#fff' : (hov ? '#152E56' : C.azul),
        color: outline ? C.textoMed : '#fff',
      }}
    >
      {label}
    </button>
  );
};

// ─── Botón paginación ─────────────────────────────────────────────────────────
const PgBtn = ({ label, onClick, active, disabled }) => {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        width: 36, height: 36, borderRadius: 8,
        border: `1px solid ${active ? C.azul : '#E2E8F0'}`,
        background: active ? C.azul : (hov ? C.azulLight : '#fff'),
        color: active ? '#fff' : C.textoMed,
        fontSize: 14, fontWeight: active ? 700 : 500,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        fontFamily: 'inherit', transition: 'all .15s',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      {label}
    </button>
  );
};

// ─── Tarjeta de vacante ───────────────────────────────────────────────────────
const VacanteCard = ({ vacante, index, onPostular }) => {
  const [hovered, setHovered] = useState(false);
  const badge = getBadge(vacante.modalidad);
  return (
    <div
      style={{
        ...st.card,
        boxShadow: hovered ? '0 8px 28px rgba(27,58,107,0.12)' : '0 1px 4px rgba(0,0,0,0.05)',
        transform: hovered ? 'translateY(-2px)' : 'none',
        borderColor: hovered ? '#BFDBFE' : C.grisBorde,
        animationDelay: `${index * 0.05}s`,
        animation: 'fadeUp 0.35s ease both',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Barra azul→rojo */}
      <div style={{ height: 4, background: `linear-gradient(90deg, ${C.azul}, ${C.rojo})`, borderRadius: '12px 12px 0 0', margin: '-1px -1px 0', position: 'relative', top: -1 }} />

      {/* Badges */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10, marginTop: 14 }}>
        <span style={st.badgeDept}>
          <Building2 size={11} style={{ flexShrink: 0 }} />
          {vacante.departamento}
        </span>
        <span style={{ ...st.badgeBase, background: badge.bg, color: badge.color, borderColor: badge.border }}>
          {vacante.modalidad}
        </span>
      </div>

      {/* Título */}
      <h3 style={st.cardTitle}>{vacante.titulo}</h3>

      {/* Descripción */}
      <p style={st.cardDesc}>{truncate(vacante.descripcion)}</p>

      {/* Meta */}
      <div style={st.cardMeta}>
        <span style={st.metaItem}><MapPin size={12} color={C.gris} />{vacante.provincia || 'Santo Domingo'}</span>
        {vacante.fecha && <span style={st.metaItem}><Calendar size={12} color={C.gris} />{fmtFecha(vacante.fecha)}</span>}
        {vacante.salarioMin && (
          <span style={st.metaItem}>
            <Award size={12} color={C.gris} />
            RD$ {Number(vacante.salarioMin).toLocaleString()}
            {vacante.salarioMax ? ` – ${Number(vacante.salarioMax).toLocaleString()}` : ''}
          </span>
        )}
      </div>

      {/* Footer */}
      <div style={st.cardFooter}>
        <span style={{ fontSize: 11, color: C.gris, display: 'flex', alignItems: 'center', gap: 4 }}>
          <Clock size={11} color={C.gris} /> Tiempo completo
        </span>
        <BtnPostular onClick={() => onPostular(vacante)} />
      </div>
    </div>
  );
};

// ─── COMPONENTE PRINCIPAL ─────────────────────────────────────────────────────
export default function VacantesPublicas() {
  const navigate = useNavigate();

  const [vacantes,         setVacantes]         = useState([]);
  const [loading,          setLoading]          = useState(true);
  const [error,            setError]            = useState(null);
  const [search,           setSearch]           = useState('');
  const [deptFilter,       setDeptFilter]       = useState('');
  const [modFilter,        setModFilter]        = useState('');
  const [page,             setPage]             = useState(1);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const PER_PAGE = 6;

  // ── Cargar vacantes del mismo servicio que FormularioPostulacion ────────────
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await postulacionesPublicasService.getVacantesPublicas();
        setVacantes(Array.isArray(data) ? data.map(normalizar) : []);
      } catch (err) {
        setError('No se pudieron cargar las vacantes. Intente nuevamente.');
        console.error('[VacantesPublicas]', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // ── Filtrado ─────────────────────────────────────────────────────────────────
  const filtered = vacantes.filter(v => {
    const q  = search.toLowerCase().trim();
    const mQ = !q || v.titulo.toLowerCase().includes(q) || v.descripcion.toLowerCase().includes(q) || v.departamento.toLowerCase().includes(q);
    const mD = !deptFilter || v.departamento === deptFilter;
    const mM = !modFilter  || v.modalidad    === modFilter;
    return mQ && mD && mM;
  });

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const paginated  = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const deptos     = [...new Set(vacantes.map(v => v.departamento))].sort();
  const hayFiltros = search || deptFilter || modFilter;

  const resetPage = useCallback(() => setPage(1), []);
  const setQ    = v => { setSearch(v);     resetPage(); };
  const setDept = v => { setDeptFilter(v); resetPage(); };
  const setMod  = v => { setModFilter(v);  resetPage(); };
  const limpiar = () => { setSearch(''); setDeptFilter(''); setModFilter(''); resetPage(); };

  // ── Navega al formulario con la vacante preseleccionada ────────────────────
  const irAPostular = (vacante) => {
    navigate('/postulacion', {
      state: { vacanteId: String(vacante.id), titulo: vacante.titulo }
    });
  };

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div style={st.page}>

      {/* ════ NAVBAR ════ */}
      <nav style={st.nav}>
        <div style={st.navInner}>
         {/*} <a href="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>*/}
          <a href="https://empleos.prodominicana.gob.do/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>

            <img
              src="data:image/svg+xml;charset=utf-8;base64,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"
              alt="ProDominicana"
              style={{ height: 38, width: 'auto' }}
              onError={e => { e.target.style.display = 'none'; }}
            />
          </a>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flex: 1 }}>
            <a href="https://empleos.prodominicana.gob.do/" style={st.navLink}>Inicio</a>

            <a href="/vacantes" style={{ ...st.navLink, color: C.azul, fontWeight: 700, borderBottom: `2px solid ${C.rojo}`, borderRadius: 0, paddingBottom: 4 }}>Vacantes</a>
          </div>
          
        </div>
      </nav>

      {/* ════ HERO HEADER ════ */}
      <div style={st.hero}>
        <div style={st.heroInner}>
          {/* Miga de pan 
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: '1rem' }}>
            <a href="/" style={{ color: C.rojo, textDecoration: 'none', fontSize: 13 }}>Inicio</a>
            <ChevronRight size={14} color="#94A3B8" />
            <span style={{ color: C.gris, fontSize: 13 }}>Vacantes</span>
          </div>*/}

          <h1 style={st.heroTitle}>
            Vacantes <span style={{ color: C.rojo }}>disponibles</span>
          </h1>
          <p style={st.heroSub}>
            Explora oportunidades laborales en el Centro de Exportación e Inversión de la República Dominicana.
          </p>

          {/* Estadísticas */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
            {[
              { icon: <Users size={15} color={C.rojo}  />, val: loading ? '—' : vacantes.length, lbl: 'Posiciones abiertas' },
              { icon: <Building2 size={15} color={C.azul}/>, val: loading ? '—' : deptos.length,  lbl: 'Departamentos' },
              { icon: <Globe size={15} color={C.azul}  />, val: 'RD',                             lbl: 'Santo Domingo' },
            ].map((s, i) => (
              <React.Fragment key={i}>
                {i > 0 && <div style={{ width: 1, height: 24, background: C.grisBorde }} />}
                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  {s.icon}
                  <span style={{ fontSize: 20, fontWeight: 800, color: C.texto, letterSpacing: '-0.02em' }}>{s.val}</span>
                  <span style={{ fontSize: 12, color: C.gris }}>{s.lbl}</span>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* ════ BARRA DE BÚSQUEDA ════ */}
      <div style={{ background: C.grisClaroS, borderBottom: `1px solid ${C.grisBorde}`, padding: '0.875rem clamp(1rem,4vw,2rem)' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>

          {/* Input texto */}
          <div style={st.searchField}>
            <Search size={15} color={C.gris} style={{ flexShrink: 0 }} />
            <input
              style={st.searchInput}
              type="text"
              placeholder="Buscar por cargo, área o descripción…"
              value={search}
              onChange={e => setQ(e.target.value)}
            />
            {search && (
              <button style={st.clearXBtn} onClick={() => setQ('')}><X size={13} /></button>
            )}
          </div>

          {/* Departamento */}
          <div style={st.selectField}>
            <Briefcase size={14} color={C.gris} style={{ flexShrink: 0 }} />
            <select style={st.selectEl} value={deptFilter} onChange={e => setDept(e.target.value)}>
              <option value="">Todos los departamentos</option>
              {deptos.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            <ChevronDown size={13} color={C.gris} style={{ flexShrink: 0, pointerEvents: 'none' }} />
          </div>

          {/* Modalidad */}
          <div style={{ ...st.selectField, minWidth: 155 }}>
            <MapPin size={14} color={C.gris} style={{ flexShrink: 0 }} />
            <select style={st.selectEl} value={modFilter} onChange={e => setMod(e.target.value)}>
              <option value="">Modalidad</option>
              <option>Presencial</option>
              <option>Híbrido</option>
              <option>Remoto</option>
            </select>
            <ChevronDown size={13} color={C.gris} style={{ flexShrink: 0, pointerEvents: 'none' }} />
          </div>

          {/* Limpiar filtros */}
          {hayFiltros && (
            <button style={st.btnLimpiar} onClick={limpiar}>
              <X size={12} /> Limpiar filtros
            </button>
          )}
        </div>
      </div>

      {/* ════ LAYOUT PRINCIPAL ════ */}
      <div style={st.main}>

        {/* ── SIDEBAR ── */}
        <aside style={st.sidebar}>

          {/* Toggle mobile */}
          <button
            className="mobile-toggle-btn"
            style={st.mobileToggle}
            onClick={() => setMobileFilterOpen(o => !o)}
          >
            <Filter size={13} />
            Filtrar categorías
            <ChevronDown size={13} style={{ marginLeft: 'auto', transform: mobileFilterOpen ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />
          </button>

          <div className="sidebar-content" style={{ display: mobileFilterOpen ? 'block' : undefined }}>

            {/* ── Categorías ── */}
            <div style={st.sideCard}>
              <div style={st.sideCardHead}>
                <span style={st.sideCardTitle}>Categorías</span>
                {deptFilter && <button style={st.smallClear} onClick={() => setDept('')}>Limpiar</button>}
              </div>
              {loading && deptos.length === 0 && (
                <div style={{ padding: '10px 14px' }}>
                  {[80, 65, 90, 55, 75].map((w, i) => (
                    <div key={i} style={{ ...st.skeletonLine, width: `${w}%`, height: 12, marginBottom: 8 }} />
                  ))}
                </div>
              )}
              {deptos.map(dept => {
                const count  = vacantes.filter(v => v.departamento === dept).length;
                const active = deptFilter === dept;
                return (
                  <label key={dept} style={{ ...st.filterItem, background: active ? C.azulLight : 'transparent' }}>
                    <input type="radio" name="dept" style={{ display: 'none' }} checked={active} onChange={() => setDept(active ? '' : dept)} />
                    <span style={{ ...st.radioCircle, borderColor: active ? C.rojo : C.grisBorde, background: active ? C.rojo : C.blanco }}>
                      {active && <span style={st.radioDot} />}
                    </span>
                    <span style={{ ...st.filterLabel, color: active ? C.azul : C.textoMed, fontWeight: active ? 600 : 400 }}>{dept}</span>
                    <span style={{ ...st.filterCount, background: active ? C.azul : C.grisClaro, color: active ? C.blanco : C.gris }}>{count}</span>
                  </label>
                );
              })}
            </div>

            {/* ── Modalidad ── */}
            <div style={st.sideCard}>
              <div style={st.sideCardHead}>
                <span style={st.sideCardTitle}>Modalidad</span>
                {modFilter && <button style={st.smallClear} onClick={() => setMod('')}>Limpiar</button>}
              </div>
              {['Presencial', 'Híbrido', 'Remoto'].map(mod => {
                const count  = vacantes.filter(v => v.modalidad === mod).length;
                const active = modFilter === mod;
                return (
                  <label key={mod} style={{ ...st.filterItem, background: active ? C.azulLight : 'transparent' }}>
                    <input type="radio" name="mod" style={{ display: 'none' }} checked={active} onChange={() => setMod(active ? '' : mod)} />
                    <span style={{ ...st.radioCircle, borderColor: active ? C.rojo : C.grisBorde, background: active ? C.rojo : C.blanco }}>
                      {active && <span style={st.radioDot} />}
                    </span>
                    <span style={{ ...st.filterLabel, color: active ? C.azul : C.textoMed, fontWeight: active ? 600 : 400 }}>{mod}</span>
                    <span style={{ ...st.filterCount, background: active ? C.azul : C.grisClaro, color: active ? C.blanco : C.gris }}>{count}</span>
                  </label>
                );
              })}
            </div>

            {/* ── CTA ── 
            <div style={{ background: C.azulLight, border: '1px solid #BFDBFE', borderRadius: 12, padding: '1rem', textAlign: 'center' }}>
              <Award size={20} color={C.rojo} />
              <p style={{ margin: '0.5rem 0', fontSize: 13, color: C.textoMed, lineHeight: 1.5 }}>
                ¿Tu perfil no encaja en ninguna vacante?
              </p>
              <button
                onClick={() => navigate('/postulacion')}
                style={{ display: 'block', width: '100%', padding: '8px 12px', borderRadius: 8, border: 'none', background: C.azul, color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', marginTop: '0.5rem' }}
              >
                Envía tu hoja de vida
              </button>
            </div> */} 
          </div>
        </aside>

        {/* ── RESULTADOS ── */}
        <section style={{ flex: 1, minWidth: 0 }}>

          {/* Contador */}
          <div style={{ marginBottom: '1rem', paddingBottom: '0.75rem', borderBottom: `1px solid ${C.grisBorde}` }}>
            {loading ? (
              <span style={{ fontSize: 14, color: C.gris, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Loader size={14} color={C.gris} style={{ animation: 'spin 1s linear infinite' }} />
                Cargando vacantes…
              </span>
            ) : (
              <span style={{ fontSize: 14, color: C.gris }}>
                Mostrando <strong style={{ color: C.texto }}>{filtered.length}</strong> de{' '}
                <strong style={{ color: C.texto }}>{vacantes.length}</strong> vacantes
                {hayFiltros && <span style={{ color: C.rojo, fontWeight: 600 }}> (con filtros activos)</span>}
              </span>
            )}
          </div>

          {/* Error */}
          {error && (
            <div style={st.errorBox}>
              <AlertCircle size={17} />
              <span style={{ flex: 1 }}>{error}</span>
              <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#991B1B', padding: 0 }} onClick={() => setError(null)}>
                <X size={14} />
              </button>
            </div>
          )}

          {/* Skeletons */}
          {loading && (
            <div style={st.grid}>
              {Array(6).fill(0).map((_, i) => <SkeletonCard key={i} />)}
            </div>
          )}

          {/* Estado vacío */}
          {!loading && filtered.length === 0 && (
            <div style={{ textAlign: 'center', padding: '4rem 2rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
              <Briefcase size={42} color="#CBD5E1" />
              <p style={{ fontWeight: 600, fontSize: 16, color: C.textoMed, margin: '0.5rem 0 0.25rem' }}>No se encontraron vacantes</p>
              <p style={{ fontSize: 13, color: C.gris, margin: 0 }}>Intenta con otros términos o categorías</p>
              {hayFiltros && (
                <button style={{ ...st.btnLimpiar, marginTop: '0.875rem' }} onClick={limpiar}>
                  <X size={12} /> Limpiar filtros
                </button>
              )}
            </div>
          )}

          {/* Grid vacantes */}
          {!loading && paginated.length > 0 && (
            <div style={st.grid}>
              {paginated.map((v, i) => (
                <VacanteCard key={v.id} vacante={v} index={i} onPostular={irAPostular} />
              ))}
            </div>
          )}

          {/* Paginación */}
          {!loading && totalPages > 1 && (
            <div style={st.pagination}>
              <PgBtn label="‹" disabled={page === 1} onClick={() => { setPage(p => p - 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
                <PgBtn key={n} label={n} active={n === page} onClick={() => { setPage(n); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />
              ))}
              <PgBtn label="›" disabled={page === totalPages} onClick={() => { setPage(p => p + 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />
            </div>
          )}
        </section>
      </div>

      {/* ════ FOOTER ════ */}
      <footer style={{ background: C.grisClaroS, borderTop: `1px solid ${C.grisBorde}`, padding: '1.25rem clamp(1rem,4vw,2rem)' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <img src="/images/PD-Logo-RGB-CEI.png" alt="ProDominicana" style={{ height: 26, opacity: 0.65 }} />
          <span style={{ color: '#94A3B8', fontSize: 12 }}>© {new Date().getFullYear()} ProDominicana — Centro de Exportación e Inversión</span>
          <span style={{ color: '#CBD5E1', fontSize: 12 }}>ProTalentHub v2.0</span>
        </div>
      </footer>

      {/* ════ ESTILOS GLOBALES ════ */}
      <style>{`
        * { box-sizing: border-box; }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes skeletonPulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.45; }
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        .sidebar-content { display: block; }
        @media (max-width: 800px) {
          .sidebar-content   { display: none; }
          .mobile-toggle-btn { display: flex !important; }
        }
        @media (min-width: 801px) {
          .mobile-toggle-btn { display: none !important; }
          .sidebar-content   { display: block !important; }
        }
      `}</style>
    </div>
  );
}

// ─── Estilos ──────────────────────────────────────────────────────────────────
const st = {
  page: { minHeight: '100vh', background: '#fff', fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif", color: '#1E293B' },

  nav: { position: 'sticky', top: 0, zIndex: 100, background: '#fff', borderBottom: '1px solid #E2E8F0', boxShadow: '0 1px 8px rgba(0,0,0,0.06)' },
  navInner: { maxWidth: 1200, margin: '0 auto', padding: '0 clamp(1rem,4vw,2rem)', height: 64, display: 'flex', alignItems: 'center', gap: '1.5rem' },
  navLink: { fontSize: 14, fontWeight: 500, color: '#475569', textDecoration: 'none', padding: '6px 12px', borderRadius: 6 },

  hero: { background: 'linear-gradient(155deg, #EEF3FB 0%, #fff 55%)', borderBottom: '1px solid #E2E8F0', padding: 'clamp(2rem,5vw,3.5rem) clamp(1rem,4vw,2rem)' },
  heroInner: { maxWidth: 1200, margin: '0 auto' },
  heroTitle: { fontSize: 'clamp(1.75rem,5vw,2.75rem)', fontWeight: 800, color: '#1B3A6B', margin: '0 0 0.5rem', letterSpacing: '-0.02em', lineHeight: 1.15 },
  heroSub: { fontSize: 15, color: '#475569', maxWidth: 560, margin: '0 0 1.5rem', lineHeight: 1.65, fontWeight: 300 },

  searchField: { flex: 2, minWidth: 220, display: 'flex', alignItems: 'center', gap: 8, background: '#fff', border: '1.5px solid #E2E8F0', borderRadius: 10, padding: '8px 12px' },
  searchInput: { flex: 1, border: 'none', outline: 'none', fontSize: 14, color: '#1E293B', background: 'transparent', fontFamily: 'inherit' },
  clearXBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', padding: 2, display: 'flex', alignItems: 'center' },
  selectField: { flex: 1, minWidth: 180, display: 'flex', alignItems: 'center', gap: 8, background: '#fff', border: '1.5px solid #E2E8F0', borderRadius: 10, padding: '8px 10px' },
  selectEl: { flex: 1, border: 'none', outline: 'none', fontSize: 13, color: '#1E293B', background: 'transparent', cursor: 'pointer', fontFamily: 'inherit', appearance: 'none' },
  btnLimpiar: { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '8px 14px', borderRadius: 8, border: '1.5px solid #DC143C', background: '#FEF0F3', color: '#DC143C', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' },

  main: { maxWidth: 1200, margin: '0 auto', padding: '2rem clamp(1rem,4vw,2rem)', display: 'flex', gap: '2rem', alignItems: 'flex-start' },

  sidebar: { width: 260, flexShrink: 0, position: 'sticky', top: 80 },
  mobileToggle: { display: 'none', width: '100%', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 10, border: '1.5px solid #E2E8F0', background: '#fff', color: '#1E293B', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', marginBottom: 8 },

  sideCard: { background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12, overflow: 'hidden', marginBottom: 12 },
  sideCardHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid #E2E8F0', background: '#F8FAFC' },
  sideCardTitle: { fontSize: 11, fontWeight: 700, color: '#1B3A6B', textTransform: 'uppercase', letterSpacing: '0.1em' },
  smallClear: { fontSize: 11, color: '#DC143C', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 },
  filterItem: { display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', cursor: 'pointer', transition: 'background .15s', userSelect: 'none' },
  radioCircle: { width: 16, height: 16, borderRadius: '50%', border: '1.5px solid', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all .15s' },
  radioDot: { width: 6, height: 6, borderRadius: '50%', background: '#fff' },
  filterLabel: { flex: 1, fontSize: 13, lineHeight: 1.3, transition: 'all .15s' },
  filterCount: { fontSize: 11, fontWeight: 600, borderRadius: 5, padding: '1px 6px', transition: 'all .15s' },

  errorBox: { display: 'flex', alignItems: 'center', gap: 8, padding: '12px 14px', borderRadius: 10, background: '#FEF2F2', border: '1px solid #FECACA', color: '#991B1B', fontSize: 13, marginBottom: 16 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))', gap: '1rem' },

  card: { background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12, padding: '1.25rem', transition: 'all .2s', display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden', animationFillMode: 'both' },
  badgeBase: { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 5, border: '1px solid' },
  badgeDept: { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 5, background: '#EEF3FB', color: '#1B3A6B', border: '1px solid #BFDBFE' },
  cardTitle: { fontSize: 15, fontWeight: 700, color: '#1E293B', margin: '0 0 0.4rem', lineHeight: 1.35 },
  cardDesc: { fontSize: 13, color: '#475569', lineHeight: 1.6, margin: '0 0 0.75rem', flex: 1 },
  cardMeta: { display: 'flex', gap: '0.65rem', flexWrap: 'wrap', marginBottom: '0.875rem' },
  metaItem: { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#64748B' },
  cardFooter: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: '0.875rem', borderTop: '1px solid #E2E8F0' },

  pagination: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6, marginTop: '2rem' },
  skeletonLine: { borderRadius: 6, background: 'linear-gradient(90deg, #F1F5F9 25%, #E2E8F0 50%, #F1F5F9 75%)', backgroundSize: '200% 100%', animation: 'skeletonPulse 1.4s ease-in-out infinite' },
};